import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, ChevronLeft, ChevronRight, Loader2, CalendarPlus, Banknote, Construction, BellRing } from "lucide-react";
import api, { cachedGet } from "@/lib/api";
import { euro, fmtDateTime, formatApiErrorDetail, toInputDateTime } from "@/lib/format";
import { localeTag } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import CalendarView from "@/components/agenda/CalendarView";
import AgendaSidebar from "@/components/agenda/AgendaSidebar";
import AppointmentDialog from "@/components/agenda/AppointmentDialog";
import PaymentDialog from "@/components/agenda/PaymentDialog";
import { siteSegments, isoDay } from "@/lib/cantieri";
import { isSimple } from "@/lib/edition";
import { apptColor, collabColor, pickAppt, localDay, SCADENZA_COLOR, PALETTE, DEFAULT_COLOR, CANTIERE_COLOR, PAGAMENTO_COLOR, PAGATO_COLOR, REMINDER_COLOR } from "@/components/agenda/palette";

const EMPTY = { titolo: "", contact_id: "", cliente_nome: "", data: "", data_fine: "", luogo: "", note: "", assigned_to: "", assigned_nome: "", colore: "" };
const VIEWS = [
  { id: "dayGridMonth", label: "Mese", testid: "agenda-view-month" },
  { id: "timeGridWeek", label: "Settimana", testid: "agenda-view-week" },
  { id: "timeGridDay", label: "Giorno", testid: "agenda-view-day" },
  { id: "listWeek", label: "Elenco", testid: "agenda-view-list" },
];
const HOUR = 3600000;

const sanitizePhone = (raw) => {
  let d = (raw || "").replace(/[^\d]/g, "");
  if (!d) return "";
  if (d.startsWith("00")) d = d.slice(2);
  if (!d.startsWith("39") && d.length <= 11) d = "39" + d;
  return d;
};

const initialView = () => localStorage.getItem("agenda-view") || (window.innerWidth < 768 ? "listWeek" : "timeGridWeek");

export default function Agenda() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isJunior = user?.role === "junior";
  const isAdmin = user?.role === "admin";
  const simple = isSimple(user);
  const calRef = useRef(null);
  const api$ = () => calRef.current?.getApi();

  const [appts, setAppts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [collabUsers, setCollabUsers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [savedColors, setCollabColors] = useState({});
  const [view, setView] = useState(initialView);
  const [title, setTitle] = useState("");
  const [cursor, setCursor] = useState(new Date());
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [filter, setFilter] = useState("tutti");
  const [assignFilter, setAssignFilter] = useState("tutti");
  const [saving, setSaving] = useState(false);
  const [selectedAppts, setSelectedAppts] = useState([]);
  const [bulkTo, setBulkTo] = useState("none");
  const [bulkSaving, setBulkSaving] = useState(false);
  const [cantieri, setCantieri] = useState([]);
  const [pagamenti, setPagamenti] = useState([]);
  const [occs, setOccs] = useState([]);
  const [payDlg, setPayDlg] = useState(null);

  const loadPayments = () => {
    const from = new Date(); from.setMonth(from.getMonth() - 6);
    const to = new Date(); to.setMonth(to.getMonth() + 18);
    api.get("/pagamenti").then((r) => setPagamenti(r.data)).catch(() => {});
    api.get("/pagamenti/occorrenze", { params: { da: isoDay(from), a: isoDay(to) } }).then((r) => setOccs(r.data)).catch(() => {});
  };
  const load = () => {
    Promise.all([cachedGet("/appointments"), cachedGet("/settings")])
      .then(([a, s]) => { setAppts(a.data); setSettings(s.data); setCollabColors(s.data?.collab_colors || {}); });
    if (!isJunior) {
      cachedGet("/contacts").then((c) => setContacts(c.data)).catch(() => {});
      if (!simple) cachedGet("/quotes").then((q) => setQuotes(q.data)).catch(() => {});
    }
    if (isAdmin) {
      cachedGet("/users").then((u) => setCollabUsers((u.data || []).filter((x) => x.role === "junior" || x.role === "staff"))).catch(() => {});
      if (!simple) api.get("/cantieri").then((r) => setCantieri(r.data)).catch(() => {});
      loadPayments();
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const collabColors = useMemo(() => {
    const free = PALETTE.filter((c) => c !== DEFAULT_COLOR);
    const auto = Object.fromEntries(collabUsers.map((u, i) => [u.id, free[i % free.length]]));
    return { ...auto, ...savedColors };
  }, [collabUsers, savedColors]);

  const show = (k) => filter === "tutti" || filter === k;
  const visibleAppts = useMemo(() => (!(filter === "tutti" || filter === "appuntamenti") ? [] : appts.filter((a) => {
    if (!isAdmin || assignFilter === "tutti") return true;
    if (assignFilter === "none") return !a.assigned_to;
    return a.assigned_to === assignFilter;
  })), [appts, filter, isAdmin, assignFilter]);
  const scadenze = useMemo(() => (isJunior || !(filter === "tutti" || filter === "scadenze") ? [] : quotes.filter((q) => q.scadenza && ["bozza", "inviato"].includes(q.stato))), [quotes, filter, isJunior]);
  const showSites = show("cantieri");
  const showPays = show("pagamenti");
  const sites = useMemo(() => (showSites ? cantieri.filter((c) => c.data_inizio && c.data_fine) : []), [showSites, cantieri]);
  const pays = useMemo(() => (showPays ? occs : []), [showPays, occs]);

  const fcEvents = useMemo(() => [
    ...visibleAppts.map((a) => {
      const color = apptColor(a);
      return {
        id: a.id, title: a.titolo, start: a.data, end: a.data_fine && new Date(a.data_fine) > new Date(a.data) ? a.data_fine : new Date(new Date(a.data).getTime() + HOUR).toISOString(),
        backgroundColor: color, borderColor: color, textColor: "#fff",
        extendedProps: { type: "appuntamento", raw: a, stripe: a.assigned_to ? collabColor(a.assigned_to, collabColors) : "", sub: [a.cliente_nome, a.assigned_nome].filter(Boolean).join(" · ") },
      };
    }),
    ...scadenze.map((q) => ({
      id: `q-${q.id}`, title: `Scadenza prev. N. ${q.numero}`, start: localDay(q.scadenza), allDay: true, editable: false,
      backgroundColor: SCADENZA_COLOR, borderColor: SCADENZA_COLOR, textColor: "#fff",
      extendedProps: { type: "scadenza", quote: q, sub: q.cliente_nome },
    })),
    ...sites.flatMap((c) => siteSegments(c.data_inizio, c.data_fine).map(([s, e], i) => ({
      id: `c-${c.id}-${i}`, title: c.calcolo === "libero" ? c.nome : `${c.nome} · ${c.num_operai} op.`, start: s, end: e, allDay: true, editable: false,
      backgroundColor: CANTIERE_COLOR, borderColor: CANTIERE_COLOR, textColor: "#fff", classNames: c.stato === "completato" ? ["opacity-60"] : [],
      extendedProps: { type: "cantiere", cantiere: c, sub: c.cliente_nome },
    }))),
    ...pays.map((o) => ({
      id: `p-${o.id}`, title: `${o.pagato ? "\u2713 " : ""}${o.titolo}${o.importo ? ` · ${euro(o.importo)}` : ""}`, start: o.data, allDay: true, editable: false,
      backgroundColor: o.pagato ? PAGATO_COLOR : PAGAMENTO_COLOR, borderColor: o.pagato ? PAGATO_COLOR : PAGAMENTO_COLOR, textColor: "#fff",
      extendedProps: { type: "pagamento", occ: o },
    })),
  ], [visibleAppts, scadenze, collabColors, sites, pays]);

  const upcoming = useMemo(() => {
    const from = new Date(new Date().setHours(0, 0, 0, 0));
    const day = (s) => new Date(`${s}T00:00:00`);
    return [
      ...visibleAppts.map((a) => ({ id: a.id, type: "appuntamento", date: a.data, title: a.titolo, sub: a.cliente_nome, raw: a })),
      ...scadenze.map((q) => ({ id: `q-${q.id}`, type: "scadenza", date: q.scadenza, title: `Scadenza prev. N. ${q.numero}`, sub: q.cliente_nome })),
      ...sites.map((c) => ({ id: `c-${c.id}`, type: "cantiere", date: day(c.data_inizio).toISOString(), title: `Inizio cantiere: ${c.nome}`, sub: c.cliente_nome, raw: c })),
      ...pays.filter((o) => !o.pagato).map((o) => ({ id: `p-${o.id}`, type: "pagamento", date: day(o.data).toISOString(), title: o.titolo, sub: o.importo ? euro(o.importo) : "", raw: o })),
    ].filter((e) => new Date(e.date) >= from).sort((a, b) => new Date(a.date) - new Date(b.date)).slice(0, 12);
  }, [visibleAppts, scadenze, sites, pays]);

  const openPayment = (o) => setPayDlg({ initial: pagamenti.find((p) => p.id === o.pagamento_id) || null, occ: o });
  const openOther = (e) => {
    if (e.type === "pagamento") openPayment(e.raw);
    else if (e.type === "cantiere") navigate(`/cantieri?view=${e.raw.id}`);
    else if (e.type === "scadenza") navigate(`/preventivi?view=${e.id.slice(2)}`);
  };

  const changeView = (v) => { setView(v); localStorage.setItem("agenda-view", v); api$()?.changeView(v); };
  const pickDate = (d) => { setCursor(d); api$()?.gotoDate(d); };

  const openNewAt = (start, end) => {
    setForm({ ...EMPTY, data: toInputDateTime(start.toISOString()), data_fine: toInputDateTime(end.toISOString()) });
    setEditId(null); setOpen(true);
  };
  const openNew = () => {
    const s = new Date(); s.setMinutes(s.getMinutes() < 30 ? 30 : 60, 0, 0);
    openNewAt(s, new Date(s.getTime() + HOUR));
  };
  const openReminder = () => { openNew(); setForm((f) => ({ ...f, colore: REMINDER_COLOR })); };
  const term = simple ? { one: "Appuntamento", many: "appuntamenti" } : { one: "Sopralluogo", many: "sopralluoghi" };
  const openEdit = (a) => {
    setForm({ ...EMPTY, ...pickAppt(a), data: toInputDateTime(a.data), data_fine: toInputDateTime(a.data_fine) });
    setEditId(a.id); setOpen(true);
  };
  const closeDialog = () => { setOpen(false); setEditId(null); };

  const onSelect = (sel) => {
    let { start, end } = sel;
    if (sel.allDay) { start = new Date(sel.start); start.setHours(9, 0, 0, 0); end = new Date(start.getTime() + HOUR); }
    api$()?.unselect();
    openNewAt(start, end);
  };
  const onEventClick = ({ event }) => {
    const p = event.extendedProps;
    if (p.type === "appuntamento") openEdit(p.raw);
    else if (p.type === "scadenza" && !isJunior) navigate(`/preventivi?view=${p.quote.id}`);
    else if (p.type === "cantiere") navigate(`/cantieri?view=${p.cantiere.id}`);
    else if (p.type === "pagamento") openPayment(p.occ);
  };
  const onMove = async (info) => {
    const a = info.event.extendedProps.raw;
    const start = info.event.start;
    const end = info.event.end || new Date(start.getTime() + HOUR);
    const payload = { ...pickAppt(a), data: start.toISOString(), data_fine: end.toISOString(), contact_id: a.contact_id || null };
    try {
      await api.put(`/appointments/${a.id}`, payload);
      setAppts((list) => list.map((x) => (x.id === a.id ? { ...x, data: payload.data, data_fine: payload.data_fine } : x)));
      toast.success("Appuntamento aggiornato", { description: fmtDateTime(payload.data) });
    } catch (e) {
      info.revert();
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const onDatesSet = (arg) => { setTitle(arg.view.title); setCursor(arg.view.currentStart); };

  const save = async () => {
    if (!form.titolo || !form.data) { toast.error("Inserisci titolo e data"); return; }
    setSaving(true);
    try {
      const payload = { ...form, data: new Date(form.data).toISOString(), data_fine: form.data_fine ? new Date(form.data_fine).toISOString() : "", contact_id: form.contact_id || null };
      if (editId) await api.put(`/appointments/${editId}`, payload);
      else await api.post("/appointments", payload);
      toast.success(editId ? "Appuntamento aggiornato" : "Appuntamento aggiunto");
      closeDialog(); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const delAppt = async (id) => { await api.delete(`/appointments/${id}`); toast.success("Appuntamento eliminato"); closeDialog(); load(); };

  const changeCollabColor = async (uid, color) => {
    const next = { ...savedColors, [uid]: color };
    setCollabColors(next);
    await api.put("/settings/collab-colors", { colors: next }).catch(() => toast.error("Colore non salvato"));
  };

  const toggleApptSel = (id) => setSelectedAppts((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const doBulkAssign = async () => {
    if (bulkTo === "none") { toast.error("Seleziona un collaboratore"); return; }
    const u = collabUsers.find((x) => x.id === bulkTo);
    setBulkSaving(true);
    try {
      await api.post("/appointments/bulk-assign", { ids: selectedAppts, assigned_to: bulkTo, assigned_nome: u?.name || u?.email || "" });
      toast.success(`${term.many.charAt(0).toUpperCase()}${term.many.slice(1)} assegnati`, { description: u?.name || u?.email || "" });
      setSelectedAppts([]); setBulkTo("none"); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBulkSaving(false); }
  };

  const waMsg = () => {
    const tag = localeTag();
    const dt = form.data ? new Date(form.data).toLocaleString(tag, { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
    const dtFine = form.data_fine ? new Date(form.data_fine).toLocaleTimeString(tag, { hour: "2-digit", minute: "2-digit" }) : "";
    return [
      `\u{1F4CD} ${term.one}: ${form.titolo || "\u2014"}`,
      dt ? `\u{1F5D3}\uFE0F ${dt}${dtFine ? ` \u2013 ${dtFine}` : ""}` : "",
      form.luogo ? `\u{1F4CC} Luogo: ${form.luogo}` : "",
      form.cliente_nome ? `\u{1F464} Cliente: ${form.cliente_nome}` : "",
      form.note ? `\u{1F4DD} Note: ${form.note}` : "",
      "",
      `\u2014 ${settings?.ragione_sociale || ""}`,
    ].filter((l) => l !== "").join("\n");
  };
  const shareTo = (c) => {
    if (!form.titolo || !form.data) { toast.error("Inserisci almeno titolo e data prima di condividere"); return; }
    const num = sanitizePhone(c.telefono);
    if (!num) { toast.error("Numero WhatsApp non valido"); return; }
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(waMsg())}`, "_blank");
  };

  return (
    <div className="space-y-4">
      {isJunior && (
        <div className="rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 text-sm" data-testid="junior-banner">
          <span className="font-semibold text-primary">I tuoi sopralluoghi.</span> Qui vedi solo gli appuntamenti che ti sono stati assegnati.
        </div>
      )}
      <div className="flex items-center justify-between flex-wrap gap-3" data-testid="agenda-toolbar">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => api$()?.today()} data-testid="agenda-today">Oggi</Button>
          <Button variant="ghost" size="icon" onClick={() => api$()?.prev()} data-testid="agenda-prev-month"><ChevronLeft className="w-5 h-5" /></Button>
          <Button variant="ghost" size="icon" onClick={() => api$()?.next()} data-testid="agenda-next-month"><ChevronRight className="w-5 h-5" /></Button>
          <h2 className="font-heading font-bold text-lg sm:text-xl capitalize ml-1" data-testid="agenda-title" translate="no">{title}</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-lg border border-border bg-secondary/50 p-0.5" role="tablist">
            {VIEWS.map((v) => (
              <button key={v.id} onClick={() => changeView(v.id)} data-testid={v.testid}
                className={`px-3 py-1.5 min-h-[40px] [@media(pointer:coarse)]:min-h-[44px] text-sm rounded-md font-medium transition-colors ${view === v.id ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                {v.label}
              </button>
            ))}
          </div>
          {!isJunior && (
            <>
              {isAdmin && (
                <Select value={assignFilter} onValueChange={setAssignFilter}>
                  <SelectTrigger className="w-44" data-testid="agenda-assign-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tutti">Tutti i collaboratori</SelectItem>
                    <SelectItem value="none">Non assegnati</SelectItem>
                    {collabUsers.map((u) => <SelectItem key={u.id} value={u.id} data-testid={`agenda-assign-filter-opt-${u.id}`}>{u.name || u.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-40" data-testid="agenda-type-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tutti">Tutti gli eventi</SelectItem>
                  <SelectItem value="appuntamenti">Appuntamenti</SelectItem>
                  {isAdmin && !simple && <SelectItem value="cantieri">Cantieri</SelectItem>}
                  {isAdmin && <SelectItem value="pagamenti">Pagamenti</SelectItem>}
                  {!simple && <SelectItem value="scadenze">Scadenze preventivi</SelectItem>}
                </SelectContent>
              </Select>
              {isAdmin ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><Button data-testid="new-appointment-trigger-button"><Plus className="w-4 h-4 mr-1.5" /> Nuovo</Button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={openNew} data-testid="new-menu-appointment"><CalendarPlus className="w-4 h-4 mr-2 text-sky-600" /> {simple ? "Appuntamento" : "Appuntamento / sopralluogo"}</DropdownMenuItem>
                    {simple && <DropdownMenuItem onClick={openReminder} data-testid="new-menu-reminder"><BellRing className="w-4 h-4 mr-2 text-amber-500" /> Promemoria</DropdownMenuItem>}
                    <DropdownMenuItem onClick={() => setPayDlg({ initial: null, occ: null })} data-testid="new-menu-payment"><Banknote className="w-4 h-4 mr-2 text-red-600" /> Promemoria pagamento</DropdownMenuItem>
                    {!simple && <DropdownMenuItem onClick={() => navigate("/cantieri?new=1")} data-testid="new-menu-site"><Construction className="w-4 h-4 mr-2 text-orange-600" /> Cantiere</DropdownMenuItem>}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : <Button onClick={openNew} data-testid="new-appointment-trigger-button"><Plus className="w-4 h-4 mr-1.5" /> Nuovo Appuntamento</Button>}
            </>
          )}
        </div>
      </div>

      {isAdmin && selectedAppts.length > 0 && (
        <div className="rounded-xl border border-primary/30 bg-primary/5 p-3 flex flex-wrap items-center gap-3" data-testid="bulk-assign-bar">
          <span className="text-sm font-medium">{selectedAppts.length} {term.many} selezionati</span>
          <Select value={bulkTo} onValueChange={setBulkTo}>
            <SelectTrigger className="w-56" data-testid="bulk-assign-select"><SelectValue placeholder="Assegna a…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Assegna a…</SelectItem>
              {collabUsers.map((u) => <SelectItem key={u.id} value={u.id} data-testid={`bulk-assign-option-${u.id}`}>{u.name || u.email} {u.role === "junior" ? "· Junior" : "· Collab."}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={doBulkAssign} disabled={bulkSaving} data-testid="bulk-assign-apply">{bulkSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Assegna in blocco"}</Button>
          <Button size="sm" variant="ghost" onClick={() => { setSelectedAppts([]); setBulkTo("none"); }}>Annulla</Button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[300px_1fr]">
        <div className="min-w-0 rounded-xl border border-border bg-card p-2 sm:p-3 h-[calc(100vh-14rem)] min-h-[560px] xl:order-2 agenda-fc" data-testid="agenda-calendar-view">
          <CalendarView ref={calRef} events={fcEvents} editable={!isJunior} initialView={view}
            onSelect={onSelect} onEventClick={onEventClick} onMove={onMove} onDatesSet={onDatesSet} />
        </div>
        <div className="xl:order-1">
          <AgendaSidebar date={cursor} onPickDate={pickDate} collabUsers={collabUsers} collabColors={collabColors} onColorChange={changeCollabColor}
            isAdmin={isAdmin} isJunior={isJunior} simple={simple} items={upcoming} selected={selectedAppts} toggleSel={toggleApptSel} onOpen={openEdit} onOpenOther={openOther} onDelete={delAppt} />
        </div>
      </div>

      <PaymentDialog open={!!payDlg} initial={payDlg?.initial} occ={payDlg?.occ} onClose={() => setPayDlg(null)} onSaved={() => { setPayDlg(null); loadPayments(); }} />

      <AppointmentDialog open={open} onClose={closeDialog} form={form} setForm={setForm} editId={editId} isJunior={isJunior} isAdmin={isAdmin}
        contacts={contacts} collabUsers={collabUsers} collabColors={collabColors} collaboratori={settings?.collaboratori || []}
        onSave={save} onDelete={delAppt} onShare={shareTo} saving={saving} simple={simple} />
    </div>
  );
}
